import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Form,
  message,
  Input,
  Button,
  Select,
  InputNumber,
  Switch,
  Popconfirm,
  Checkbox,
} from "antd";
import { Container, Row, Col } from "react-bootstrap";
import PageHeader from "../../../component/pageHeaders";
import { API } from "../../../config/API";
import { GET, POST, PUT, DELETE } from "../../../utils/apirequest";
import {
  COUNTRIES,
  DEFAULT_CURRENCY,
} from "../../../config/countries";
import { loadEpaperConfig, useEpaperConfig } from "../../../config/epaperConfig";

// Optional per-country prices live in form fields "country_price_<CODE>".
// Empty: AED countries pay the base price, free plans stay free, and other
// countries have no price — the backend refuses those registrations.
const countryPriceField = (code: string) => `country_price_${code}`;

const parsePrices = (raw: any) => {
  if (typeof raw !== "string") return raw && typeof raw === "object" ? raw : null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const countryPricesToFields = (raw: any) => {
  const prices = parsePrices(raw);
  const fields: Record<string, number | undefined> = {};
  for (const c of COUNTRIES) {
    const entry = prices?.[c.code];
    fields[countryPriceField(c.code)] =
      entry && entry.price !== undefined && entry.price !== null
        ? Number(entry.price)
        : undefined;
  }
  return fields;
};

// `original` keeps prices for countries this form doesn't show (e.g. one
// deleted from E-Paper Settings) so saving never drops them silently.
const fieldsToCountryPrices = (values: any, original?: any) => {
  const prices: Record<string, { currency: string; price: number }> = {};
  const shown = new Set(COUNTRIES.map((c) => c.code));
  for (const [code, entry] of Object.entries(parsePrices(original) || {})) {
    if (!shown.has(code)) prices[code] = entry as any;
  }
  for (const c of COUNTRIES) {
    const v = values?.[countryPriceField(c.code)];
    if (v !== undefined && v !== null && v !== "") {
      prices[c.code] = { currency: c.currency, price: Number(v) };
    }
  }
  return Object.keys(prices).length ? prices : null;
};

const TYPE_OPTIONS = [
  { value: "free", label: "Free" },
  { value: "trial", label: "Trial" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
  { value: "print_combo", label: "Print Combo" },
  { value: "custom", label: "Custom" },
];

const ROLE_OPTIONS = [
  { id: 4, name: "Admin" },
  { id: 6, name: "Epaper Admin" },
  { id: 5, name: "Epaper Agent" },
];

const REGION_OPTIONS = [
  { label: "India", value: "india" },
  { label: "Gulf", value: "gulf" },
];

function CreateSubscriptionPlan() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [error, setError] = useState<any>({});
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const originalPrices = useRef<any>(null);
  useEpaperConfig();
  const basePrice = Number(Form.useWatch("price", form) || 0);
  const formValues = Form.useWatch([], form) || {};

  useEffect(() => {
    if (id !== "create") {
      LoadData(id);
    }
  }, []);

  // Fills only the empty country prices with each country's default amount.
  const fillDefaultPrices = () => {
    const fields: Record<string, number> = {};
    for (const c of COUNTRIES) {
      const v = form.getFieldValue(countryPriceField(c.code));
      if (v === undefined || v === null || v === "") {
        fields[countryPriceField(c.code)] = c.defaultAmount;
      }
    }
    form.setFieldsValue(fields);
  };

  const LoadData = async (planId: any) => {
    setLoading2(true);
    try {
      // Countries first, so every saved country price gets its field.
      await loadEpaperConfig();
      const res: any = await GET(
        `${API.SUBSCRIPTION_PLAN_ONE}${planId}`,
        null,
        null
      );
      const plan = res?.data;
      if (plan?.id) {
        originalPrices.current = plan?.country_prices ?? null;
        form.setFieldsValue({
          plan_name: plan?.plan_name,
          type: plan?.type,
          duration_type: plan?.duration_type,
          duration_value: plan?.duration_value,
          price: Number(plan?.price),
          is_active: plan?.is_active !== false,
          sort_order: plan?.sort_order ?? 0,
          status: plan?.status || "active",
          region: plan?.region || undefined,
          description: plan?.description || "",
          ...countryPricesToFields(plan?.country_prices),
        });
        setSelectedRoles(
          Array.isArray(plan?.allowed_role_ids)
            ? plan.allowed_role_ids.map(Number)
            : []
        );
      }
    } catch (err) {
      console.error("Error loading plan:", err);
      message.error("Could not load plan.");
    } finally {
      setLoading2(false);
    }
  };

  const toggleRole = (roleId: number) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((r) => r !== roleId)
        : [...prev, roleId]
    );
  };

  const toggleAllRoles = (checked: boolean) => {
    setSelectedRoles(checked ? ROLE_OPTIONS.map((r) => r.id) : []);
  };

  const submitPlan = async (body: any) => {
    const isCreate = id === "create";
    const url = isCreate
      ? API.SUBSCRIPTION_PLANS
      : `${API.SUBSCRIPTION_PLAN_ONE}${id}`;
    const METHOD = isCreate ? POST : PUT;
    try {
      const response: any = await METHOD(url, body);
      if (response?.error) {
        const code = response.error?.code;
        if (code === "invalid_price") {
          setError({ price: response.error.message });
        }
        message.error(response.error.message || "Could not save plan.");
        return;
      }
      if (response?.data?.id) {
        message.success(
          isCreate ? "Plan created successfully." : "Plan updated successfully."
        );
        navigate(-1);
        return;
      }
      message.error(
        isCreate ? "Could not create plan." : "Could not update plan."
      );
    } catch (err: any) {
      const errBody = err?.data?.error ?? err?.error;
      const msg =
        errBody?.message ||
        (isCreate ? "Could not create plan." : "Could not update plan.");
      if (errBody?.code === "invalid_price") {
        setError({ price: msg });
      }
      message.error(msg);
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const body: any = {
        plan_name: String(values?.plan_name || "").trim(),
        type: values?.type,
        duration_type: values?.duration_type,
        duration_value: Number(values?.duration_value),
        price: Number(values?.price),
        renewal_type: "manual",
        is_active: values?.is_active !== false,
        description: values?.description || null,
        sort_order: Number.isFinite(Number(values?.sort_order))
          ? Number(values.sort_order)
          : 0,
        status: values?.status || "active",
        region: values?.region || null,
        country_prices: fieldsToCountryPrices(values, originalPrices.current),
        allowed_role_ids: selectedRoles,
      };
      await submitPlan(body);
    } finally {
      setLoading(false);
    }
  };

  const deleteData = async (planId: any) => {
    setIsLoading(true);
    try {
      const res: any = await DELETE(`${API.SUBSCRIPTION_PLAN_ONE}${planId}`);
      if (res?.error) {
        message.error(res.error.message || "Could not delete plan.");
      } else {
        message.success("Plan deleted");
        navigate(`/Auth/subscription-plans?page=1&limit=10`);
      }
    } catch (err: any) {
      const msg = err?.error?.message || "Could not delete plan.";
      message.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const clearPriceError = () => {
    setError((prev: any) => {
      if (prev && typeof prev === "object" && prev.price) {
        const { price, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  };

  return (
    <div>
      <PageHeader
        title={"Subscription Plans"}
        loader={loading2}
        length={`Dashboard / Subscription Plans / ${
          id === "create" ? "Create" : "Edit"
        } Plan`}
      >
        {id !== "create" && (
          <>
            <div style={{ width: 10 }} />
            <div>
              <Popconfirm
                placement="bottom"
                title={"Delete Plan"}
                description={"Are you sure you want to delete this plan?"}
                okText="Yes"
                cancelText="No"
                onConfirm={() => deleteData(id)}
              >
                <Button loading={isLoading} danger>
                  Delete Plan
                </Button>
              </Popconfirm>
            </div>
          </>
        )}
      </PageHeader>

      <Form
        form={form}
        onFinish={onFinish}
        name="subscription-plan-form"
        scrollToFirstError
        initialValues={{
          duration_type: "months",
          duration_value: 1,
          price: 0,
          is_active: true,
          sort_order: 0,
          status: "active",
          type: "monthly",
          region: "india",
        }}
      >
        <Container fluid>
          <Row style={{ padding: 10 }}>
            <Col sm={8} xs={12}>
              <Row>
                <Col sm="6" xs="12">
                  <label className="newsFormLabels">Plan Name</label>
                  <Form.Item
                    name="plan_name"
                    rules={[
                      { required: true, message: "Please enter plan name" },
                      { max: 120, message: "Max 120 characters" },
                    ]}
                  >
                    <Input size="large" placeholder="e.g. Print Subscriber - 1 Year Free" />
                  </Form.Item>

                  <label className="newsFormLabels">Type</label>
                  <Form.Item
                    name="type"
                    rules={[{ required: true, message: "Please select a type" }]}
                  >
                    <Select size="large" placeholder="Select plan type">
                      {TYPE_OPTIONS.map((o) => (
                        <Select.Option key={o.value} value={o.value}>
                          {o.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Row>
                    <Col sm="6" xs="12">
                      <label className="newsFormLabels">Duration Type</label>
                      <Form.Item
                        name="duration_type"
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Select size="large">
                          <Select.Option value="days">Days</Select.Option>
                          <Select.Option value="months">Months</Select.Option>
                          <Select.Option value="years">Years</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col sm="6" xs="12">
                      <label className="newsFormLabels">Duration Value</label>
                      <Form.Item
                        name="duration_value"
                        rules={[
                          { required: true, message: "Required" },
                          {
                            validator: (_: any, value: any) =>
                              Number(value) > 0
                                ? Promise.resolve()
                                : Promise.reject("Must be greater than 0"),
                          },
                        ]}
                      >
                        <InputNumber
                          size="large"
                          min={1}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>

                <Col sm="6" xs="12">
                  <Row>
                    <Col sm="12" xs="12">
                      <label className="newsFormLabels">Price</label>
                      <Form.Item
                        name="price"
                        validateStatus={error["price"] ? "error" : ""}
                        help={error["price"]}
                        rules={[
                          { required: true, message: "Required" },
                          {
                            validator: (_: any, value: any) =>
                              Number(value) >= 0
                                ? Promise.resolve()
                                : Promise.reject("Must be ≥ 0"),
                          },
                        ]}
                      >
                        <InputNumber
                          size="large"
                          min={0}
                          style={{ width: "100%" }}
                          onChange={clearPriceError}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row>
                    <Col sm="6" xs="12">
                      <label className="newsFormLabels">Status</label>
                      <Form.Item
                        name="status"
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Select size="large">
                          <Select.Option value="active">Active</Select.Option>
                          <Select.Option value="archived">Archived</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col sm="6" xs="12">
                      <label className="newsFormLabels">Sort Order</label>
                      <Form.Item name="sort_order">
                        <InputNumber
                          size="large"
                          style={{ width: "100%" }}
                          placeholder="0"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <label className="newsFormLabels">Region</label>
                  <Form.Item
                    name="region"
                    rules={[{ required: true, message: "Please select a region" }]}
                  >
                    <Select
                      size="large"
                      placeholder="Select Region"
                      options={REGION_OPTIONS}
                      allowClear
                    />
                  </Form.Item>

                  <label className="newsFormLabels">Is Active</label>
                  <Form.Item name="is_active" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col sm="12" xs="12">
                  <label className="newsFormLabels">Description</label>
                  <Form.Item name="description">
                    <Input.TextArea
                      rows={3}
                      placeholder="Internal / display description for this plan"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col sm="12" xs="12">
                  <label className="newsFormLabels">Country Prices</label>
                  <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
                    Empty: AED countries charge the base price and free plans
                    stay free. For a paid plan, other countries need a price
                    here — registrations for them are refused without one.
                    Placeholders show each country's default amount from
                    E-Paper Settings.
                  </div>
                  {basePrice > 0 ? (
                    <Button size="small" style={{ marginBottom: 12 }} onClick={fillDefaultPrices}>
                      Fill empty prices with default amounts
                    </Button>
                  ) : null}
                </Col>
                {COUNTRIES.map((c) => {
                  const v = formValues[countryPriceField(c.code)];
                  const empty = v === undefined || v === null || v === "";
                  const blocked =
                    empty &&
                    basePrice > 0 &&
                    c.currency !== DEFAULT_CURRENCY &&
                    !(c.defaultAmount > 0);
                  return (
                  <Col sm="4" xs="6" key={c.code}>
                    <label className="newsFormLabels">
                      {c.name}
                      {c.isActive ? "" : " (inactive)"}
                    </label>
                    <Form.Item
                      name={countryPriceField(c.code)}
                      validateStatus={blocked ? "warning" : undefined}
                      help={
                        blocked
                          ? "No price or default amount — registrations blocked"
                          : undefined
                      }
                      rules={[
                        {
                          validator: (_: any, value: any) =>
                            value === undefined ||
                            value === null ||
                            Number(value) >= 0
                              ? Promise.resolve()
                              : Promise.reject("Must be ≥ 0"),
                        },
                      ]}
                    >
                      <InputNumber
                        size="large"
                        min={0}
                        addonBefore={c.currency}
                        style={{ width: "100%" }}
                        placeholder={
                          c.currency === DEFAULT_CURRENCY
                            ? "Base price"
                            : c.defaultAmount > 0
                              ? `Default ${c.defaultAmount}`
                              : "No default"
                        }
                      />
                    </Form.Item>
                  </Col>
                  );
                })}
              </Row>
            </Col>

            <Col sm={4} xs={12}>
              <div className="userfrom-box">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <div>
                    <label
                      className="newsFormLabels"
                      style={{ marginBottom: 0 }}
                    >
                      Allowed Roles
                    </label>
                  </div>
                  <div>
                    <Checkbox
                      onChange={(e) => toggleAllRoles(e.target.checked)}
                      checked={
                        ROLE_OPTIONS.length > 0 &&
                        selectedRoles.length === ROLE_OPTIONS.length
                      }
                      indeterminate={
                        selectedRoles.length > 0 &&
                        selectedRoles.length < ROLE_OPTIONS.length
                      }
                    >
                      Select All
                    </Checkbox>
                  </div>
                </div>

                <div
                  style={{ fontSize: 12, color: "#777", marginBottom: 8 }}
                >
                  Leave empty to make this plan visible to everyone.
                </div>

                <div className="user-access-list">
                  {ROLE_OPTIONS.map((role) => (
                    <div className="userfrom-roles-item" key={role.id}>
                      <Checkbox
                        checked={selectedRoles.includes(role.id)}
                        onChange={() => toggleRole(role.id)}
                      >
                        {role.name}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <div style={{ height: "9vh" }} />
        <div className="FixedFooter">
          <Container fluid>
            <Row>
              <Col sm={9} xs={12}></Col>
              <Col sm={1} xs={6}>
                <Button
                  size="large"
                  type="text"
                  danger
                  block
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
              </Col>
              <Col sm={2} xs={6}>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={loading}
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Form>
    </div>
  );
}

export default CreateSubscriptionPlan;
